import React, { useCallback } from 'react';
import {
  Box,
  IBoxProps,
  Inline,
} from '@styleseat/ui';
import { BrandColor, toRGBA } from '@styleseat/brand';
import { Pressable } from 'react-native';
import Image from '../../Image';
import {
  Absolute,
  BodyText,
  Stack,
} from '../../../ui';
import { BasicCameraCreateIcon } from '../../../ui/Icons';
import {
  EmptyableImage,
  ImageRow,
  isEmpty,
} from './types';
import { useInTest, useIsMobile } from '../../../../hooks';
import { GridGridDynamicIcon } from '../../../ui/Icons/GridGridDynamicIcon';
import { CLIENT_PROPROFILE_CLARIFY_LOCATION_TEST_NAME } from '../constants';
import { clickable } from '../../../../styles';

const Square: React.FCWithChildren<{ children?: React.ReactNode } & IBoxProps> = ({
  children,
  ...restProps
}) => (
  <Box
    minWidth={100}
    minHeight={100}
    flex={1}
  >
    <Box
      flex={1}
      style={{
        paddingTop: '100%',
      }}
    >
      <Absolute
        top={0}
        bottom={0}
        left={0}
        right={0}
        {...restProps}
      >
        {children}
      </Absolute>
    </Box>
  </Box>
);

type ProfileImageGridItemProps = {
  overallIndex: number;
  image: EmptyableImage;
  isEditMode?: boolean;
  onImageClick?: (image: EmptyableImage, index: number) => void;
  isSeeMore?: boolean;
  formatImageUrl: (url: string) => string;
};

const ProfileImageGridItem: React.FCWithChildren<ProfileImageGridItemProps> = ({
  overallIndex,
  image,
  isEditMode,
  isSeeMore,
  onImageClick,
  formatImageUrl,
}) => {
  const handleImageClick = useCallback(
    () => onImageClick(image, overallIndex),
    [
      image,
      overallIndex,
      onImageClick,
    ],
  );

  if (isEmpty(image)) {
    if (isEditMode) {
      return (
        <Pressable
          onPress={handleImageClick}
          testID={`profile-image-grid-empty-item-${overallIndex}`}
          style={{
            flex: 1,
            minHeight: 100,
            minWidth: 100,
          }}
        >
          <Square
            justifyContent="center"
            alignItems="center"
            color={BrandColor.LightGrey}
            style={clickable}
          >
            <BasicCameraCreateIcon height={24} width={24} />
          </Square>
        </Pressable>
      );
    }

    return (
      <Square
        color={BrandColor.LightGrey}
        testID={`profile-image-grid-empty-item-${overallIndex}`}
      />
    );
  }

  return (
    <Pressable
      onPress={handleImageClick}
      testID={`profile-image-grid-item-${overallIndex}`}
      style={{
        flex: 1,
        minHeight: 100,
        minWidth: 100,
      }}
    >
      <Square style={clickable}>
        <Image
          source={formatImageUrl(image.url)}
          style={{
            width: '100%',
            height: '100%',
          }}
          accessibilityLabel={image.alt_text || image.blurb}
        />
        {isSeeMore && (
          <Absolute
            top={0}
            bottom={0}
            left={0}
            right={0}
          >
            <Stack
              justifyContent="center"
              alignItems="center"
              flex={1}
              color={toRGBA(BrandColor.BackInBlack, 0.45)}
            >
              <GridGridDynamicIcon
                color={BrandColor.WhiteWash}
                width={20}
                height={20}
              />
              <Box
                paddingTop={4}
                borderBottomColor={BrandColor.WhiteWash}
                borderStyle="solid"
                borderBottomWidth={2}
              >

                <BodyText color={BrandColor.WhiteWash} weight="bold">See All</BodyText>
              </Box>
            </Stack>
          </Absolute>
        )}
      </Square>
    </Pressable>
  );
};

type DeprecatedProfileImageGridProps = {
  isEditMode?: boolean;
  imageRows: Array<ImageRow>;
  imageRowSize: number;
  hasMore?: boolean;
  onImageClick?: (image: EmptyableImage, index: number) => void;
  formatImageUrl: (url: string) => string;
  disableBottomPadding?: boolean;
};

export const DeprecatedProfileImageGrid: React.FCWithChildren<DeprecatedProfileImageGridProps> = ({
  isEditMode,
  imageRows,
  imageRowSize,
  hasMore,
  onImageClick,
  formatImageUrl,
  disableBottomPadding = false,
}) => {
  const isMobile = useIsMobile();
  const inLocationTest = useInTest(CLIENT_PROPROFILE_CLARIFY_LOCATION_TEST_NAME);
  const hasPaddingBottom = !disableBottomPadding && isMobile && !inLocationTest;

  return (
    <Stack
      space={1}
      paddingBottom={hasPaddingBottom ? 12 : 0}
    >
      {imageRows.map((row: ImageRow, rowIdx: number) => (
        <React.Fragment key={row.key}>
          <Inline
            justifyContent="center"
            alignItems="stretch"
            space={1}
          >
            {row.images.map((image: EmptyableImage, imageIdx: number) => (
              <ProfileImageGridItem
                key={`${row.key}-image-${image.id}`}
                overallIndex={imageIdx + rowIdx * imageRowSize}
                image={image}
                onImageClick={onImageClick}
                formatImageUrl={formatImageUrl}
                isEditMode={isEditMode}
                isSeeMore={(
                hasMore
                && rowIdx === imageRows.length - 1
                && imageIdx === imageRowSize - 1
                )}
              />
            ))}
          </Inline>
        </React.Fragment>
      ))}
    </Stack>
  );
};
